import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useConnectionStatus } from './graphql-connection-provider';
import { Container, Center, Spinner } from '@vitvio/design-system';

/**
 * Guard component that redirects to no-connection page on initial load or when navigating
 * if there's no connection to the server.
 *
 * This component should be used as a route guard to wrap protected routes.
 * - On initial load: checks connection status and redirects if connection was never successful
 * - On route changes: redirects to no-connection page if app is offline
 * Runtime connection loss (after successful initial connection) is handled by the ConnectionLossBanner component.
 */
export function InitialConnectionGuard() {
  const { isConnected, isChecking } = useConnectionStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const hasPassedInitialCheckRef = useRef(false);

  // On navigation (or hard refresh) while offline, redirect to no-connection.
  // Only react to location changes so we do not redirect on runtime connection loss.
  useEffect(() => {
    if (!hasPassedInitialCheckRef.current) {
      return;
    }
    if (!isConnected) {
      navigate('/no-connection', { replace: true });
    }
    // We only want to react to location changes, not changes in the connection status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  // TODO: this is a temporary fix to resolve a circular dependency issue with the shared components library
  if (!hasPassedInitialCheckRef.current && isChecking) {
    return (
      <Container centerContent minH="100vh">
        <Center>
          <Spinner size="xl" />
        </Center>
      </Container>
    );
  }

  if (!hasPassedInitialCheckRef.current && !isChecking && !isConnected) {
    return <Navigate to="/no-connection" replace />;
  }

  if (!hasPassedInitialCheckRef.current && !isChecking && isConnected) {
    hasPassedInitialCheckRef.current = true;
  }

  return <Outlet />;
}
